/**
 * MERMA — lo que pierde de peso lo que se vende al peso.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  LA MISMA REGLA QUE EL SERVIDOR, EN TODAS LAS PANTALLAS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * «El queso pierde peso»: se seca, se le quita la corteza, se desmorona al
 * cortarlo. De cada kilo que se compra se vende menos de un kilo. Lo pidió el
 * dueño, para la venta normal y para la cocina.
 *
 * Con 5 %: para que lleguen 100 g al cliente —o a la arepa— salen del estante
 * 105,3 g, y lo que cuestan es lo que cuestan esos 105,3 g. Es `con_merma` en
 * `catalog/models.py`, y tienen que seguir dando lo mismo: si la pantalla y el
 * servidor calcularan distinto, la ganancia de la ficha y la del reporte no
 * cuadrarían.
 */

/** Solo lo que se vende al peso pierde peso. Igual que `UNIDADES_CON_MERMA`. */
const UNIDADES_CON_MERMA = new Set(["kg", "g"]);

type Merma = string | number | null | undefined;
type Unidad = string | null | undefined;

export function llevaMerma(unidad: Unidad): boolean {
  return UNIDADES_CON_MERMA.has((unidad ?? "").toLowerCase());
}

/** El porcentaje que de verdad aplica. `0` si no se vende al peso o no pierde. */
export function mermaQueAplica(merma: Merma, unidad: Unidad): number {
  if (!llevaMerma(unidad) || merma == null || merma === "") return 0;
  const m =
    typeof merma === "number" ? merma : Number.parseFloat(String(merma).replace(",", "."));
  return Number.isFinite(m) && m > 0 && m < 100 ? m : 0;
}

/**
 * Lo que tiene que salir para que queden `cantidad`: 1 kg con 5 % → 1,0526 kg.
 *
 * Sirve igual para el costo: $5 el kilo con 5 % son $5,26 por kilo vendido.
 */
export function conMerma(cantidad: number, merma: Merma, unidad: Unidad): number {
  const m = mermaQueAplica(merma, unidad);
  return m > 0 ? (cantidad * 100) / (100 - m) : cantidad;
}

/**
 * El costo por unidad vendida, como texto, para las cuentas de ganancia.
 *
 * Sin merma devuelve el costo tal cual, sin tocarlo: redondearlo cambiaría la
 * ganancia de un producto solo por abrir su ficha.
 */
export function costoConMerma(costo: string | null | undefined, merma: Merma, unidad: Unidad): string {
  const texto = costo ?? "";
  const n = Number.parseFloat(texto);
  if (!Number.isFinite(n) || mermaQueAplica(merma, unidad) === 0) return texto;
  return conMerma(n, merma, unidad).toFixed(4);
}

/**
 * «105,3 g»: lo que sale del inventario por una cantidad de receta.
 *
 * `null` si no pierde. Siempre en gramos: una receta lleva «100 g de queso»,
 * no «0,105 kg».
 */
export function loQueSaleDelInventario(cantidad: number, merma: Merma, unidad: Unidad): string | null {
  if (mermaQueAplica(merma, unidad) === 0 || !(cantidad > 0)) return null;
  const sale = conMerma(cantidad, merma, unidad);
  const gramos = (unidad ?? "").toLowerCase() === "kg" ? sale * 1000 : sale;
  return `${gramos.toLocaleString("es", { maximumFractionDigits: 1 })} g`;
}

/** «5», «2,5»: el porcentaje para escribirlo en una frase. */
export function porcentajeParaLeer(merma: Merma): string {
  const m =
    typeof merma === "number"
      ? merma
      : Number.parseFloat(String(merma ?? "0").replace(",", "."));
  return Number.isFinite(m) ? m.toLocaleString("es", { maximumFractionDigits: 2 }) : "0";
}
